import { useEffect, useState, type ReactElement } from 'react';
import { toast } from '../../../design-system/components';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { apiErrorCode, emberApi, type Knock, type ReadNote, type ReadRow, type ShareAskRow } from '../api';
import { useSession } from '../../auth';
import { trackFailed } from '../../../../../shared-web/track';
import { Link } from './Link';
import { knockHeadline, letterState, unseenNotes } from './knock-letters';
import { notifyKnocksChanged } from './use-knocks';
import { QuestionCard } from './QuestionCard';
import { RoomInviteCard } from './RoomInvitation';
import { NoteComposer, NoteThread, OTHER_SIDE } from './ReadNotes';
import { InboundRegistrationCTA } from './InboundRegistrationCTA';
import './KnockCard.css';

/**
 * A letter's body (v4.0 §4.9) — one router, every kind, every placement (the
 * chat thread, the paper, the notebook, the owner's Ember page): the ask
 * whether Ember may let people read an Ember of this person's (ShareAskCard),
 * a read this person is a side of (ReadCard), a question (QuestionCard), a
 * room invitation (RoomInviteCard). Consent is a button, never a sentence the
 * model has to parse.
 */
export type KnockCardFrame = 'card' | 'letter';

export function KnockCard({ knock, onChange, frame = 'card' }: {
  knock: Knock;
  /** The row after a server-accepted change. */
  onChange?: (next: Knock) => void;
  /**
   * `card`: the panel with its own head (the notebook, the Ember page).
   * `letter`: the body of the opened letter (2026-09-07) — the page already
   * carries the headline and the Ember it is about, so the body names
   * neither, and wears no panel: the letter's paper is the panel.
   */
  frame?: KnockCardFrame;
}): ReactElement | null {
  if (knock.role === 'asker' || knock.role === 'answerer') return <QuestionCard row={knock} onChange={onChange} frame={frame} />;
  if (knock.role === 'member') return <RoomInviteCard row={knock} onChange={onChange} frame={frame} />;
  if (knock.role === 'share') {
    return (
      <ShareAskCard
        ask={{ ember_id: knock.ember.id, title: knock.ember.title, asked_line: knock.asked_line || '' }}
        answer={knock.answer}
        frame={frame}
        onAnswered={(answer) => onChange?.({ ...knock, answer: answer === 'never' ? 'no' : answer, letter: null })}
      />
    );
  }
  return <ReadCard row={knock} onChange={onChange} frame={frame} />;
}

export type ShareAnswer = 'yes' | 'no' | 'never';

/**
 * The ask (§4.9, 2026-09-27 拍板): Ember found something in this person's
 * Ember that could genuinely help someone, and asks — in its own words —
 * whether it may let people it could help read it. Three answers: yes, not
 * this one, never (the account switch goes off). The same card stands in
 * the thread after the turn Ember voiced it in, on the paper, and on the
 * notebook card's door.
 */
const SHARE_REPLY: Record<ShareAnswer, string> = { yes: 'Yes, let them', no: 'Not this one', never: 'Don’t ask me again' };

export function ShareAskCard({ ask, answer, frame = 'card', onAnswered, onReply }: {
  ask: { ember_id: string; title: string; asked_line?: string };
  /** The answer already given, or null while the ask waits. */
  answer: 'yes' | 'no' | null;
  frame?: KnockCardFrame;
  onAnswered?: (answer: ShareAnswer) => void;
  /**
   * In the thread (2026-09-28) the three answers are quick replies: the
   * button's words go into the conversation as the person's own line and
   * Ember answers them there (the letter is read off that line). Returns
   * whether the line was sent; the card then leaves — the thread carries it.
   * `answer` is the button's own value, for the caller to put on the record.
   */
  onReply?: (text: string, answer: ShareAnswer) => Promise<boolean>;
}): ReactElement | null {
  const [busy, setBusy] = useState<ShareAnswer | null>(null);
  // The answer given on this card, in this sitting — `never` has its own
  // sentence, which the row (yes | no) cannot carry.
  const [given, setGiven] = useState<ShareAnswer | null>(null);
  const [replied, setReplied] = useState(false);
  const { status: sessionStatus, refresh: refreshSession } = useSession();
  const settled: ShareAnswer | null = given ?? answer;
  const title = ask.title ? `“${ask.title}”` : 'your Ember';

  const send = async (next: ShareAnswer): Promise<void> => {
    if (busy || settled || replied) return;
    setBusy(next);
    if (onReply) {
      try {
        if (await onReply(SHARE_REPLY[next], next)) setReplied(true);
      } finally {
        setBusy(null);
      }
      return;
    }
    try {
      await emberApi.answerShare(ask.ember_id, next);
      setGiven(next);
      onAnswered?.(next);
      // `never` turned the account's switch off: the session carries it.
      if (next === 'never' && sessionStatus === 'authenticated') void refreshSession();
      notifyKnocksChanged();
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === 'already_answered') {
        // Answered elsewhere (a text, another tab): the card is stale, not wrong.
        toast.info('You already answered this one.');
        notifyKnocksChanged();
      } else {
        toast.error("Couldn't save that. Try again.");
        trackFailed('share_answer', code || 'error', { ember_id: ask.ember_id });
      }
    } finally {
      setBusy(null);
    }
  };

  const headline = settled === 'yes' ? 'You said yes.' : settled ? 'All right.' : 'Something in your Ember shines.';
  if (replied) return null;
  return (
    <section
      className={`knock-card is-share is-${settled ?? 'asked'}${frame === 'letter' ? ' is-letter' : ''}`}
      aria-label="Ember is asking about your Ember"
      data-log-seen="share_ask_card"
      data-log-state={settled ?? 'pending'}
      data-log-ember-id={ask.ember_id}
    >
      {frame === 'card' && (
        <div className="knock-head">
          <span className="knock-mark" aria-hidden>{settled ? <Dot /> : <EmberFlame className="knock-flame" />}</span>
          <h2 className="knock-title">{headline}</h2>
        </div>
      )}
      {settled === null ? (
        <>
          {frame === 'letter' && ask.asked_line
            ? <blockquote className="knock-situation"><p dir="auto">{ask.asked_line}</p></blockquote>
            : <p className="knock-lede">There’s something in {title} that could really help someone.</p>}
          <p className="knock-lede">
            May I let people it could help read it? They’d read it here, on Ember — never who you are. You can close it any time.
          </p>
          <div className="knock-actions">
            <button
              type="button"
              className={`ember-btn ember-btn-primary${busy === 'yes' ? ' is-busy' : ''}`}
              disabled={busy !== null}
              data-log="share_yes"
              data-log-ember-id={ask.ember_id}
              onClick={() => { void send('yes'); }}
            >
              Yes, let them
            </button>
            <button
              type="button"
              className="ember-btn ember-btn-secondary"
              disabled={busy !== null}
              data-log="share_no"
              data-log-ember-id={ask.ember_id}
              onClick={() => { void send('no'); }}
            >
              Not this one
            </button>
          </div>
          <button
            type="button"
            className="knock-more-door share-never"
            disabled={busy !== null}
            data-log="share_never"
            data-log-ember-id={ask.ember_id}
            onClick={() => { void send('never'); }}
          >
            Don’t ask me again
          </button>
        </>
      ) : (
        <p className="knock-lede">
          {settled === 'yes'
            ? 'Thank you. When someone is standing where you stood, Ember may show them.'
            : settled === 'no'
              ? 'Kept to yourself. Nothing changed.'
              : 'Ember won’t ask about your Embers again. You can change this in Settings.'}
        </p>
      )}
    </section>
  );
}

/**
 * A read on the paper (§4.9): for the teller, a person Ember let read their
 * Ember and what that person wrote — with a line back and the door to the
 * Ember's page; for the reader, what the teller wrote back and the door to
 * read it again. Showing it is the look: the other side's notes are marked
 * seen, and the letter ends.
 */
function ReadCard({ row, onChange, frame }: { row: ReadRow; onChange?: (next: Knock) => void; frame: KnockCardFrame }): ReactElement {
  const { status: sessionStatus } = useSession();
  const me = row.role === 'read_owner' ? 'owner' : 'reader';
  const other = me === 'owner' ? 'reader' : 'owner';
  const title = row.ember.title ? `“${row.ember.title}”` : 'your Ember';

  useEffect(() => {
    if (!unseenNotes(row)) return;
    emberApi.markReadSeen(row.read_id)
      .then(() => onChange?.({ ...row, letter: null, notes: row.notes.map((n) => (n.by === other ? { ...n, seen: true } : n)) }))
      .catch(() => { /* seen again next visit */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.read_id]);

  const addNote = (note: ReadNote): void => onChange?.({ ...row, notes: [...row.notes, note] });
  const closed = row.state === 'closed';

  return (
    <Panel row={row} frame={frame}>
      {me === 'owner' ? (
        <>
          <p className="knock-lede">
            {row.state === 'accepted'
              ? `Ember gave ${frame === 'letter' ? 'it' : title} to a person standing where you stood. They haven’t opened it yet.`
              : closed
                ? `You closed ${frame === 'letter' ? 'it' : title} to this person. What they wrote stays here.`
                : `A person standing where you stood read ${frame === 'letter' ? 'it' : title}. They never learn who you are.`}
          </p>
          <NoteThread notes={row.notes} me="owner" />
          {!closed && (
            <NoteComposer readId={row.read_id} emberId={row.ember.id} logState="owner" placeholder={row.notes.length ? 'A line back, if you like.' : 'A line for them, if you like.'} onNote={addNote} />
          )}
          {sessionStatus === 'authenticated' ? (
            <p className="knock-more-doors">
              <Link to={`/me/embers/${encodeURIComponent(row.ember.id)}`} className="knock-more-door">
                Open the Ember’s page
                <Chevron />
              </Link>
            </p>
          ) : (
            // A guest teller (2026-09-28): the notebook is a signed-in thing; the
            // note and the line back are here, and the one-text door is the way
            // to the Ember's page.
            <p className="knock-more-doors knock-join">
              <span>Right now this lives only in this browser. One text and it&rsquo;s yours on every device.</span>
              <InboundRegistrationCTA logName="letter_read_join" label="Join Ember" />
            </p>
          )}
        </>
      ) : (
        <>
          <p className="knock-lede">
            {closed
              ? `${OTHER_SIDE.owner} closed it. Nothing of yours changed.`
              : row.notes.some((n) => n.by === 'owner')
                ? `${OTHER_SIDE.owner} wrote back.`
                : `Someone let you read ${frame === 'letter' ? 'their Ember' : title}. They never learn who you are.`}
          </p>
          <NoteThread notes={row.notes} me="reader" />
          {!closed && (
            <p className="knock-more-doors">
              <Link to={`/read/${encodeURIComponent(row.read_id)}`} className="knock-more-door" data-log="read_card_open" data-log-read-id={row.read_id} data-log-ember-id={row.ember.id}>
                Read it again
                <Chevron />
              </Link>
            </p>
          )}
        </>
      )}
    </Panel>
  );
}

function Panel({ row, frame, children }: {
  row: ShareAskRow | ReadRow;
  frame: KnockCardFrame;
  children: ReactElement | ReactElement[] | (ReactElement | false | null)[];
}): ReactElement {
  const pending = row.letter !== null;
  return (
    <section
      className={`knock-card is-${row.role}${row.role !== 'share' && row.state === 'closed' ? ' is-closed' : ''}${frame === 'letter' ? ' is-letter' : ''}`}
      aria-label="A letter from Ember"
      data-log-seen="knock_card"
      data-log-state={letterState(row)}
      data-log-match-id={row.match_id}
    >
      {frame === 'card' ? (
        <div className="knock-head">
          <span className="knock-mark" aria-hidden>
            {pending ? <EmberFlame className="knock-flame" /> : <Dot />}
          </span>
          <h2 className="knock-title">{knockHeadline(row)}</h2>
        </div>
      ) : null}
      {children}
    </section>
  );
}

function Dot(): ReactElement {
  return <span className="knock-dot" />;
}

function Chevron(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="m9 6 6 6-6 6" /></svg>
  );
}
